import { client } from "../supabase";

// ─── AMORTIZACIÓN DE COMPROMISO DE PAGO (P1.10 redefinido) ─────────────────────
//
// Un compromiso de pago es una MEMORIA DE TESORERÍA (NIC 37, ni pasivo ni gasto) — vive solo en la
// proyección de Futuros, nunca escribe `movimientos_caja`/ER/ledger del proveedor. El pago real
// sigue su camino de siempre (SII/externa/sin factura/a cuenta/etc.); este subledger registra, por
// separado, qué compromiso quedó "amortizado" por ese pago. Diseño completo:
// docs/decisiones/2026-10-03-compromiso-pago-redefinicion-fase0.md (§2-§5, §8). Migración:
// supabase/migrations/20261003c_compromiso_pago_amortizacion.sql.
//
// Molde: src/utils/capex/getSaldosCapexPorTarget.js (saldo DERIVADO, 10.245) y
// src/utils/capex/saldarCapexTargets.js (wrapper de RPC).

/**
 * Batch del saldo amortizado por compromiso desde el subledger `amortizaciones_compromiso`. UNA
 * sola query por país (10.234). Devuelve Map<compromiso_id, { amortizado, moneda }> — Σ
 * `monto_target` VIGENTE (`reversado_at IS NULL`), en la moneda del compromiso (10.246, nunca se
 * convierte). El consumidor computa `saldo_pendiente = compromiso.monto - (map.get(id)?.amortizado || 0)`.
 */
export const getSaldosAmortizacionPorCompromiso = async (pais) => {
  const map = new Map();
  if (!pais) return map;
  const { data, error } = await client
    .from("amortizaciones_compromiso")
    .select("compromiso_id, monto_target, moneda_target")
    .eq("pais", pais)
    .is("reversado_at", null);
  if (error) throw error;
  for (const e of data || []) {
    const acc = map.get(e.compromiso_id) || { amortizado: 0, moneda: e.moneda_target };
    acc.amortizado += Number(e.monto_target || 0);
    map.set(e.compromiso_id, acc);
  }
  return map;
};

/**
 * Detalle de amortizaciones VIGENTES de un compromiso, con el movimiento bancario embebido (fecha,
 * descripción, monto) — para el panel "Ver amortizaciones" de EstadoCuenta/Futuros. Una sola FK entre
 * las dos tablas (10.139: sin ambigüedad de embed). Orden más reciente primero.
 * @returns {Promise<Array<{id, fecha, monto_target, moneda_target, monto_banco, moneda_banco, movimiento}>>}
 */
export const getAmortizacionesDetalladas = async (compromisoId) => {
  const { data, error } = await client
    .from("amortizaciones_compromiso")
    .select(
      "id, fecha, monto_target, moneda_target, monto_banco, moneda_banco, movimiento:movimientos_bancarios(id, descripcion_raw, fecha)"
    )
    .eq("compromiso_id", compromisoId)
    .is("reversado_at", null)
    .order("fecha", { ascending: false });
  if (error) throw error;
  return data || [];
};

/**
 * Wrapper de la RPC `amortizar_compromiso`. Precondición (en la base, 10.177): el movimiento ya
 * está `procesado` por su camino real — esta llamada es SIEMPRE un paso aparte, posterior y nunca en
 * la misma transacción que el pago (§4.2 del diseño). Rechaza con el saldo máximo disponible
 * embebido en el mensaje de error cuando excede el saldo del compromiso o del movimiento (8.6).
 * @returns {Promise<{amortizacion_id:number, saldo_pendiente:number}>}
 */
export const amortizarCompromiso = async ({ movimientoBancarioId, compromisoId, monto, usuarioId }) => {
  const { data, error } = await client.rpc("amortizar_compromiso", {
    p_movimiento_bancario_id: movimientoBancarioId,
    p_compromiso_id: compromisoId,
    p_monto: monto,
    p_usuario_id: usuarioId ?? null,
  });
  if (error) throw error;
  return data;
};

/**
 * Wrapper de la RPC `cerrar_compromiso` (8.1): pasa el compromiso a `estado='cerrado'`. Deja de
 * proyectarse en Futuros conservando el historial de amortizaciones intacto — distinto de
 * `eliminarCompromiso` (borrado lógico completo). Exige `estado='planificado'` (RAISE si no).
 * @returns {Promise<{compromiso_id:number, estado:'cerrado'}>}
 */
export const cerrarCompromiso = async ({ compromisoId, usuarioId }) => {
  const { data, error } = await client.rpc("cerrar_compromiso", {
    p_compromiso_id: compromisoId,
    p_usuario_id: usuarioId ?? null,
  });
  if (error) throw error;
  return data;
};
